/**
 * Staff comps — analytics for structured force-close "free" orders.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMP_TYPES, COMP_TYPE_LABELS, staffMealRecipientFromNote, type CompType } from '@cafe-pos/shared';
import type { Database } from '@cafe-pos/supabase';
import { Gift, Receipt, Wallet } from 'lucide-react';
import { supabase } from '../supabase.js';
import {
  Callout,
  Card,
  CardHeader,
  DateRange,
  EmptyState,
  PageHeader,
  resolveDateRangeDates,
  Stat,
  Stats,
  type DateRangeValue,
} from '../ui/index.js';

const AMD = (n: number) => `${Math.round(n).toLocaleString('en-US')} ֏`;
type Period = DateRangeValue;

type CompOrderRow = Pick<
  Database['public']['Tables']['orders']['Row'],
  'id' | 'total' | 'comp_type' | 'comp_recipient_id' | 'close_reason' | 'forced_close' | 'paid_at' | 'created_at'
>;

type RecipientRow = Pick<Database['public']['Tables']['staff']['Row'], 'id' | 'name'>;

type WasteMealRow = {
  id: string;
  recipientKey: string;
  recipientName: string;
  amd: number;
};

const FETCH_PAGE = 1000;

async function fetchPaged<T>(
  query: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += FETCH_PAGE) {
    const { data, error } = await query(offset, offset + FETCH_PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < FETCH_PAGE) break;
  }
  return rows;
}

export function StaffCompsView({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>({ preset: '30d' });
  const [orders, setOrders] = useState<CompOrderRow[]>([]);
  const [wasteMeals, setWasteMeals] = useState<WasteMealRow[]>([]);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase || !clientId) return;
    if (period.preset === 'custom' && (!period.from || !period.to)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const [start, end] = resolveDateRangeDates(period);

    void (async () => {
      try {
        const db = supabase;
        const [orderRows, recipientRows, wasteRows] = await Promise.all([
          fetchPaged((offset, limit) =>
            db
              .from('orders')
              .select('id, total, comp_type, comp_recipient_id, close_reason, forced_close, paid_at, created_at')
              .eq('client_id', clientId)
              .eq('forced_close', true)
              .eq('close_reason', 'free')
              .gte('paid_at', start.toISOString())
              .lte('paid_at', end.toISOString())
              .order('paid_at', { ascending: false })
              .range(offset, limit),
          ) as Promise<CompOrderRow[]>,
          db.from('staff').select('id, name').eq('client_id', clientId),
          fetchPaged((offset, limit) =>
            db
              .from('inventory_transactions')
              .select('id, ingredient_id, delta, note, created_at, reverted_at')
              .eq('client_id', clientId)
              .eq('type', 'waste')
              .is('reverted_at', null)
              .ilike('note', 'staff_meal%')
              .gte('created_at', start.toISOString())
              .lte('created_at', end.toISOString())
              .order('created_at', { ascending: false })
              .range(offset, limit),
          ) as Promise<
            Pick<
              Database['public']['Tables']['inventory_transactions']['Row'],
              'id' | 'ingredient_id' | 'delta' | 'note' | 'created_at' | 'reverted_at'
            >[]
          >,
        ]);
        if (cancelled) return;
        if (recipientRows.error) throw new Error(recipientRows.error.message);
        const recipientList = (recipientRows.data ?? []) as RecipientRow[];
        const byName = new Map(recipientList.map((r) => [r.name.trim().toLowerCase(), r]));

        const ingredientIds = [...new Set(wasteRows.map((w) => w.ingredient_id))];
        const costByIng = new Map<string, number>();
        if (ingredientIds.length > 0) {
          const { data: ings, error: ingErr } = await db
            .from('ingredients')
            .select('id, cost_price')
            .eq('client_id', clientId)
            .in('id', ingredientIds);
          if (ingErr) throw new Error(ingErr.message);
          for (const ing of ings ?? []) {
            costByIng.set(ing.id, Number(ing.cost_price) || 0);
          }
        }

        const meals: WasteMealRow[] = [];
        for (const w of wasteRows) {
          const nameFromNote = staffMealRecipientFromNote(w.note);
          const match = nameFromNote ? byName.get(nameFromNote.toLowerCase()) : undefined;
          const recipientKey = match?.id ?? (nameFromNote ? `name:${nameFromNote.toLowerCase()}` : '__none__');
          meals.push({
            id: w.id,
            recipientKey,
            recipientName: match?.name ?? nameFromNote ?? '',
            amd: Math.abs(Number(w.delta) || 0) * (costByIng.get(w.ingredient_id) ?? 0),
          });
        }

        setOrders(orderRows);
        setWasteMeals(meals);
        setRecipients(recipientList);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId, period]);

  const recipientName = useMemo(() => {
    const map = new Map(recipients.map((r) => [r.id, r.name]));
    return (id: string | null) => (id ? (map.get(id) ?? t('staffComps.unknownRecipient')) : '—');
  }, [recipients, t]);

  const stats = useMemo(() => {
    let totalAmd = 0;
    const byRecipient = new Map<string, { name: string; orders: number; amd: number }>();
    const byType = new Map<CompType, { orders: number; amd: number }>();
    for (const type of COMP_TYPES) byType.set(type, { orders: 0, amd: 0 });

    function addRecipient(key: string, name: string, amd: number) {
      const rec = byRecipient.get(key) ?? { name, orders: 0, amd: 0 };
      rec.orders += 1;
      rec.amd += amd;
      byRecipient.set(key, rec);
    }

    for (const o of orders) {
      const amd = Number(o.total) || 0;
      totalAmd += amd;
      const type = (COMP_TYPES as readonly string[]).includes(o.comp_type ?? '')
        ? (o.comp_type as CompType)
        : 'other';
      const typeStat = byType.get(type)!;
      typeStat.orders += 1;
      typeStat.amd += amd;

      const rid = o.comp_recipient_id ?? '__none__';
      const name =
        rid === '__none__'
          ? t('staffComps.unassigned')
          : recipientName(o.comp_recipient_id);
      addRecipient(rid, name, amd);
    }

    const mealType = byType.get('staff_meal')!;
    for (const meal of wasteMeals) {
      totalAmd += meal.amd;
      mealType.orders += 1;
      mealType.amd += meal.amd;
      addRecipient(meal.recipientKey, meal.recipientName || t('staffComps.unassigned'), meal.amd);
    }

    const recipientRows = [...byRecipient.values()].sort((a, b) => b.amd - a.amd);
    const typeRows = COMP_TYPES.map((type) => ({
      type,
      label: COMP_TYPE_LABELS[type],
      ...byType.get(type)!,
    })).filter((r) => r.orders > 0);

    return {
      count: orders.length + wasteMeals.length,
      totalAmd,
      recipientRows,
      typeRows,
    };
  }, [orders, wasteMeals, recipientName, t]);

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('staffComps.title')}
        subtitle={t('staffComps.subtitle')}
        actions={
          <DateRange value={period} onChange={setPeriod} />
        }
      />

      {error && (
        <Callout tone="danger" title={t('staffComps.couldntLoad')}>
          {error}
        </Callout>
      )}

      {loading ? (
        <div className="dash-loading">
          <div className="spinner" /> {t('common.loading')}
        </div>
      ) : (
        <>
          <Stats>
            <Stat icon={<Receipt />} label={t('staffComps.kpiOrders')} value={stats.count} />
            <Stat icon={<Gift />} label={t('staffComps.kpiTotal')} value={AMD(stats.totalAmd)} />
            <Stat
              icon={<Wallet />}
              label={t('staffComps.kpiAvg')}
              value={stats.count > 0 ? AMD(stats.totalAmd / stats.count) : '—'}
            />
          </Stats>

          <Card layout="flush">
            <CardHeader title={t('staffComps.byRecipient')} />
            {stats.recipientRows.length === 0 ? (
              <EmptyState compact title={t('staffComps.noData')} />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('staffComps.colRecipient')}</th>
                    <th className="num">{t('staffComps.colOrders')}</th>
                    <th className="num">{t('staffComps.colTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recipientRows.map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      <td className="num">{r.orders}</td>
                      <td className="num">{AMD(r.amd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card layout="flush">
            <CardHeader title={t('staffComps.byType')} />
            {stats.typeRows.length === 0 ? (
              <EmptyState compact title={t('staffComps.noData')} />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('staffComps.colType')}</th>
                    <th className="num">{t('staffComps.colOrders')}</th>
                    <th className="num">{t('staffComps.colTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.typeRows.map((r) => (
                    <tr key={r.type}>
                      <td>{t(`compTypes.${r.type}`, { defaultValue: r.label })}</td>
                      <td className="num">{r.orders}</td>
                      <td className="num">{AMD(r.amd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </section>
  );
}
